import { ROUTE_URLS } from "src/Shared/Enums/RouteUrls.enum";

/** The single parameter that carries a post-login destination. */
export const REDIRECT_PARAM = "redirect";

/**
 * The login URL for someone who asked for a page while signed out, carrying the whole URL they
 * wanted so they land back on it.
 *
 * The old form appended the requested page's raw query string to the login route, which threw the
 * path away and kept only the query — so a bookmarked or shared deep link could never return to the
 * right place (bug 5670). The destination is passed whole and encoded, so a query of its own can
 * never be mistaken for the destination.
 */
export const buildLoginRedirect = (pathname: string, search: string = ""): string => {
  const intended = `${pathname || ""}${search || ""}`;
  if (!intended || intended === ROUTE_URLS.LOGIN) return ROUTE_URLS.LOGIN;
  return `${ROUTE_URLS.LOGIN}?${REDIRECT_PARAM}=${encodeURIComponent(intended)}`;
};

/**
 * Where to go once signed in: the named destination if one was carried, otherwise Home.
 *
 * Only the named parameter is honoured. Reading any query string as a destination — splitting it on
 * the first "=" and navigating to what followed — is what sent `?step=3` to `/3`, `?section=contacts`
 * to `/contacts` and `?focus=<id>` to `/<id>`, none of which match a route, so every one of them
 * fell through to the Home grid (bugs 5670, 5707).
 *
 * An absolute URL is refused: a destination is only ever a path inside this app, so a crafted link
 * cannot use the login round trip to bounce someone to another site.
 */
export const resolveLoginDestination = (search: string = ""): string => {
  const raw = new URLSearchParams(search || "").get(REDIRECT_PARAM);
  if (!raw) return ROUTE_URLS.HOME;

  let destination: string;
  try {
    destination = decodeURIComponent(raw);
  } catch {
    return ROUTE_URLS.HOME;
  }

  destination = destination.trim();

  // Must be a single-slash-rooted path: "//host" and "https://host" are both off-site.
  if (!destination.startsWith("/") || destination.startsWith("//")) return ROUTE_URLS.HOME;
  if (destination === ROUTE_URLS.LOGIN) return ROUTE_URLS.HOME;

  return destination;
};
